/**
 * Badge Component
 * 
 * A small status indicator component with multiple variants.
 */

import React from 'react';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}

const variantStyles: Record<string, string> = {
  default: 'bg-slate-700 text-slate-200 border-slate-600',
  primary: 'bg-cyan-600/20 text-cyan-400 border-cyan-500/30',
  secondary: 'bg-slate-600/20 text-slate-300 border-slate-500/30',
  success: 'bg-green-600/20 text-green-400 border-green-500/30',
  warning: 'bg-amber-600/20 text-amber-400 border-amber-500/30',
  destructive: 'bg-red-600/20 text-red-400 border-red-500/30',
  outline: 'bg-transparent text-slate-300 border-slate-500',
};

const sizeStyles: Record<string, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className = '', variant = 'default', size = 'md', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center justify-center
          font-medium rounded-full border
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;

